import { apiClient } from './client';
import type { CareerDto, EducationDto, RecordSummaryDto } from './hr-records';
import type { CredentialDto } from './hr-trainings';
import type { AssignmentDto } from './hr-assignment';

/**
 * 직원 관리 · 입사 처리 — 근태 3단계 (2026-08-24).
 *
 * 서버: `apps/chat-server/src/modules/hr/employee-admin.routes.ts`.
 * 설계: `docs/attendance-round2-sample.html` 화면 A · 화면 B · 제안 1.
 *
 * **화면은 하나, 고칠 수 있는 칸은 사람마다 다르다.** 상세는 세 묶음(본인·인사·계정)으로 오고,
 * 각 묶음의 열림 여부(`editable`)를 서버가 함께 준다 — 화면이 스스로 판단하지 않는다.
 */

export const EMPLOYMENT_TYPE_LABEL: Record<string, string> = {
  REGULAR: '정규직', CONTRACT: '계약직', PARTTIME: '단시간',
  // 2026-09-13 · 서버 #1222 — 수습 · 인턴(서버 EMPLOYMENT_TYPES 와 같은 순서). 다국어 이름은 lib/employment-type-i18n.
  PROBATION: '수습', INTERN: '인턴',
};
export const EMPLOYMENT_STATUS_LABEL: Record<string, string> = {
  ACTIVE: '재직', LEAVE: '휴직', RESIGNED: '퇴사',
};

export interface EmployeeRowDto {
  userId: string;
  name: string;
  email: string;
  employeeNumber: string | null;
  position: string | null;
  jobLevel: number | null;
  teamId: string | null;
  teamName: string | null;
  /** 부서(본부) 이름 — 2026-09-13 #1231 에 더해진 칸(CSV 「부서」). 옛 서버는 안 준다. */
  divisionName?: string | null;
  hireDate: string | null;
  tenureLabel: string | null;
  employmentType: string | null;
  employmentStatus: string;
  isActive: boolean;
  role: string;
  /** 입사일이 없으면 null — 연차 계산이 시작되지 않는다. */
  annualDays: number | null;
  /* ★ 2026-09-13 직원관리 서버 칸(계약 §18.3~18.5) — 옛 서버는 안 준다(없으면 종전처럼 그린다). */
  /** 근무지. 비어 있으면 본사 소속으로 본다. */
  worksiteId?: string | null;
  worksiteName?: string | null;
  /** 입사 예정(입사일 > 오늘) — 재직 칸에 세지 않는다. */
  upcomingHire?: boolean;
  /** 수습 종료(전환일) `YYYY-MM-DD` — 고용형태가 수습이고 입사일이 있을 때만. */
  probationEndDate?: string | null;
  /** 그 사람의 필수 인사정보 입력률(%) — 아홉 칸 기준, 정수(계약 §18.6). */
  inputRate?: number;
  /* ★ 2026-09-14 계약 §18.4 「퇴사 예정 칸」 — 옛 서버는 안 준다. 재직 판정은 바뀌지 않는다(퇴사 예정자도 ACTIVE). */
  /** 진행 중 퇴사 처리(OPEN)의 퇴사일(마지막 근무일) `YYYY-MM-DD`. 없으면 `null`. */
  resignDate?: string | null;
  /** 퇴사 예정 — 진행 중 퇴사일이 오늘 이후 또는 당일. 퇴사일이 지났는데 OPEN 이면 `false`(화면은 「처리 대기」). */
  upcomingResign?: boolean;
}

/** 수습 기간 N 의 출처 — 회사 설정 표가 생기기 전에는 `DEFAULT`(기본 3개월). */
export type ProbationMonthsSource = 'DEFAULT' | 'INSTITUTION';

/** 지금 맡은 직책(보임) — 계약 §18.3. */
export interface EmployeeJobRoleDto {
  id: string; jobRoleId: string; name: string; callName: string | null; level: number | null; isHead: boolean;
  orgKind: 'TEAM' | 'DIVISION' | 'COMPANY'; orgId: string | null; orgName: string | null;
  isConcurrent: boolean; startDate: string;
}

/** 상위자 — 보여 주기용 파생값(결재선은 이 값을 읽지 않는다). */
export interface EmployeeSupervisorDto {
  userId: string; name: string; source: 'TEAM_LEADER' | 'DIVISION_LEADER' | 'REPRESENTATIVE';
}

export interface EmployeeListDto {
  total: number; missingHireDate: number; rows: EmployeeRowDto[];
  /* ★ 2026-09-13 계약 §18.4 · §18.5 — status 거르기와 무관하게 센다. 옛 서버는 안 준다. */
  upcomingHires?: number;
  /** `overdue` = 전환일이 이미 지났는데 고용형태가 아직 수습인 인원(§18.5 · #1230). */
  probation?: { inProbation: number; endingWithin30Days: number; overdue?: number };
  /** 필수 인사정보 입력 집계(§18.6) — 아홉 칸 · 분모는 재직자(입사 예정 제외). `label` 은 칸 이름만(「누락」 은 화면이 붙인다). */
  inputCompletion?: { total: number; items: Array<{ key: string; label: string; missing: number }> };
  probationMonths?: number;
  probationMonthsSource?: ProbationMonthsSource;
  /** 퇴사 예정 인원(§18.4 「퇴사 예정 칸」) — status 거르기와 무관하게 센다. 옛 서버는 안 준다. */
  upcomingResigns?: number;
}

export interface EmployeeDetailDto {
  /** 본인만 고치는 칸. */
  self: { name: string; phone: string | null; extension: string | null; avatarUrl: string | null };
  /** 인사 담당이 고치는 칸. */
  hr: {
    hireDate: string | null; tenureLabel: string | null; position: string | null; jobLevel: number | null;
    employmentType: string | null; employmentStatus: string;
    teamId: string | null; teamName: string | null; divisionName: string | null;
    annualDays: number | null;
    /* ★ 2026-09-13 계약 §18.3~18.5 — 옛 서버는 안 준다(`undefined` = 칸 없음, `null` = 값 없음). */
    upcomingHire?: boolean;
    /** 퇴사 예정(§18.4 「퇴사 예정 칸」 · 목록 줄과 같다) — 옛 서버는 안 준다. */
    resignDate?: string | null;
    upcomingResign?: boolean;
    probationEndDate?: string | null;
    probationMonthsSource?: ProbationMonthsSource;
    worksiteId?: string | null;
    worksiteName?: string | null;
    /** 지금 맡은 직책 — 본직 먼저 → 높은 직책 먼저. */
    jobRoles?: EmployeeJobRoleDto[];
    /** 호칭 — 대표 직책의 callName/name, 직책이 없으면 직위. */
    title?: string | null;
    supervisor?: EmployeeSupervisorDto | null;
    /** 입력률(%) — 아홉 칸 서버 집계(§18.6). 대상 여부와 무관하게 그 사람의 값. */
    inputRate?: number;
  };
  /** 회사 관리자가 고치는 칸 — 여기서는 **보기만** 한다(고치는 길은 관리 콘솔 하나). */
  account: { email: string; employeeNumber: string | null; role: string; isActive: boolean };
  /**
   * 적용 근무제 — 지각 기준을 설명하기 위해 함께 온다.
   * ★ `id`·`source` 는 2026-09-02 에 보탰다(옛 서버는 안 준다 — 화면이 없으면 없는 대로 그린다).
   */
  schedule: {
    id?: string | null; name: string; type: string; baseStart: string; baseEnd: string;
    source?: 'USER' | 'TEAM' | 'INSTITUTION' | 'DEFAULT';
  };
  editable: { SELF: boolean; HR: boolean; ACCOUNT: boolean };
}

/** `status` 는 `ACTIVE`(입사 예정 제외) · `UPCOMING` · `LEAVE` · `RESIGNED` — 계약 §18.4. */
export async function listEmployees(params?: { search?: string; teamId?: string; status?: string; worksiteId?: string }): Promise<EmployeeListDto> {
  const { data } = await apiClient.get('/hr/employees', { params });
  return data;
}

/**
 * 월별 재직 인원 추이 (2026-09-14 · 계약 §18.4 뒤 `GET /hr/employees/headcount-trend?months=` · 인사 담당).
 * ★ 재직상태 이력 표가 없어 날짜 근거로 거꾸로 센 **추정치**다(`estimated`) — 화면은 「추정치」라고 적는다.
 * ★ `months[]` 는 오래된 달이 앞 · 지난달까지는 그 달 말일 · 이번 달은 `asOf`(오늘) 기준.
 */
export interface HeadcountTrendDto {
  estimated: boolean;
  asOf: string;
  months: Array<{ month: string; active: number }>;
  /** 모든 달에서 뺀 사람 수 — 입사일 없음(삭제 계정 포함) · 퇴직/휴직인데 날짜 근거 없음. */
  excluded: { noHireDate: number; undatedStatus: number };
  basis: string[];
}

/** `months` 1~12(기본 6) — 그 밖은 서버 400. */
export async function getHeadcountTrend(months = 6): Promise<HeadcountTrendDto> {
  const { data } = await apiClient.get<HeadcountTrendDto>('/hr/employees/headcount-trend', { params: { months } });
  return data;
}

export async function getEmployee(userId: string): Promise<EmployeeDetailDto> {
  const { data } = await apiClient.get<EmployeeDetailDto>(`/hr/employees/${userId}`);
  return data;
}

/** 인사 묶음만 저장한다. 계정 칸을 섞어 보내면 서버가 400(`ACCOUNT_FIELD_NOT_HERE`)으로 거절한다. */
export async function updateEmployeeHr(userId: string, body: {
  hireDate?: string | null; position?: string | null; jobLevel?: number | null;
  employmentType?: string | null; employmentStatus?: string | null;
  /**
   * 본인 잠금 칸 (2026-09-13 · 계약 §17.2 · 서버 #1201) — 본인은 403, **인사가 여기서** 고친다.
   * `name` 1~50자 · 비울 수 없다(`null` 불가). `nameEn` ≤100자 또는 `null`. `birthday` `"MM-DD"` 또는 `null`.
   * ★ 옛 서버는 이 세 칸을 스키마에서 **조용히 지운다**(200 인데 안 바뀐다) — 서버와 함께 배포한다.
   */
  name?: string; nameEn?: string | null; birthday?: string | null;
  /** 근무지(계약 §18.3) — 사업장 id 또는 `null`(본사 소속). 이 회사 사업장이 아니면 400 `WORKSITE_NOT_FOUND`. */
  worksiteId?: string | null;
}): Promise<{ userId: string; annualDays: number | null }> {
  const { data } = await apiClient.patch(`/hr/employees/${userId}`, body);
  return data;
}

/**
 * 입사 처리 — 계정 + 인사 + 그 해 연차를 한 번에.
 *
 * 역할은 **STAFF 고정**이고, 초기 비밀번호는 **이 응답에만 한 번** 실린다(저장하지 않으므로 다시 못 본다).
 */
export async function hireEmployee(body: {
  name: string; email: string; employeeNumber?: string | null; hireDate?: string | null;
  position?: string | null; jobLevel?: number | null; employmentType?: string | null; teamId?: string | null;
}): Promise<{ userId: string; annualDays: number | null; initialPassword: string }> {
  const { data } = await apiClient.post('/hr/employees', body);
  return data;
}

/**
 * 명부 CSV 내려받기 (계약 §18.7 · 서버 #1231) — JSON 목록과 **같은 거르기**, 파일은 서버가 만든다(감사 · BOM · 이름).
 * 이메일 · 전화 · 생일은 담지 않는다. 이름은 `content-disposition` 의 `filename*`, 못 읽으면 기본 이름(근태 CSV 와 같은 꼴).
 */
/** 인사카드 — 계약 §18.7 `GET /hr/employees/:id/card`. 기록 묶음은 각 인사기록 API 와 **같은 모양**이다. */
export interface EmployeeCardDto {
  /** 만든 시각(ISO) — 인쇄물에 적는다. */
  generatedAt: string;
  person: {
    userId: string; name: string; nameEn: string | null; employeeNumber: string | null;
    position: string | null; jobLevel: number | null; employmentType: string | null;
    employmentStatus: string; upcomingHire: boolean; hireDate: string | null; tenureLabel: string | null;
    divisionName: string | null; teamName: string | null; worksiteName: string | null; title: string | null;
    jobRoles: Array<{ name: string; orgName: string | null; isConcurrent: boolean; startDate: string }>;
    phone: string | null; birthday: string | null;
  };
  careers: CareerDto[];
  educations: EducationDto[];
  recordsSummary: RecordSummaryDto;
  credentials: CredentialDto[];
  /** 시행된 발령만. */
  assignments: AssignmentDto[];
  /** 가족 표가 아직 없다(순서 8 마이그레이션 뒤) — 늘 null. */
  family: null;
}

/** 인사카드 JSON — 본인 또는 인사 담당. 부를 때마다 서버 감사(`hr.employee.card_exported`)에 남는다. */
export async function getEmployeeCard(userId: string): Promise<EmployeeCardDto> {
  const { data } = await apiClient.get<EmployeeCardDto>(`/hr/employees/${userId}/card`);
  return data;
}

export async function downloadEmployeesCsv(
  params?: { search?: string; teamId?: string; status?: string; worksiteId?: string },
): Promise<{ blob: Blob; filename: string }> {
  const res = await apiClient.get('/hr/employees', { params: { ...params, format: 'csv' }, responseType: 'blob' });
  const cd = String(res.headers['content-disposition'] ?? '');
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  return { blob: res.data as Blob, filename: m ? decodeURIComponent(m[1]!) : '직원명부.csv' };
}
